import { otherColors } from '@ehrTheme/colors';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  debounce,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { ErxSearchMedicationsResponse } from '@oystehr/sdk';
import { DateTime } from 'luxon';
import { FC, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useCommandPaletteSource } from 'src/hooks/useCommandPaletteSource';
import { useMergedMedicationHistoryQuickPicks } from 'src/hooks/useMergedQuickPicks';
import { usePendingQuickPick } from 'src/hooks/usePendingQuickPick';
import { MedicationDTO } from 'utils/lib/types/api/chart-data/chart-data.types';
import { MedicationHistoryQuickPickData } from 'utils/lib/types/api/quick-picks.types';
import { useChartData } from '../../../hooks/useChartData';
import { useGetAppointmentAccessibility } from '../../../hooks/useGetAppointmentAccessibility';
import { ExtractObjectType, useGetMedicationsSearch } from '../../../stores/appointment/appointment.queries';
import { ProviderSideListSkeleton } from '../../ProviderSideListSkeleton';
import { QuickPicksButton } from '../../QuickPicksButton';
import { CurrentMedicationGroup } from './CurrentMedicationGroup';
import { ExternalMedicationSelection } from './ExternalRxSuggestions';

interface CurrentMedicationsProviderColumnForm {
  medication: ExtractObjectType<ErxSearchMedicationsResponse> | null;
  type: MedicationDTO['type'];
  date: DateTime | null;
  lastFilledDate: DateTime | null;
  refills: string | null;
  dose: string | null;
  patientCouldNotConfirmDosage: boolean;
}

const REFILLS_PATTERN = /^\d{1,2}$/;

export interface MedicationDataProps {
  medications: MedicationDTO[];
  isLoading: boolean;
  onRemove: (resourceId: string) => Promise<void>;
}

interface CurrentMedicationsProviderColumnProps {
  medicationData: MedicationDataProps;
  onAddMedication: (selection: ExternalMedicationSelection) => Promise<boolean>;
}

export const CurrentMedicationsProviderColumn: FC<CurrentMedicationsProviderColumnProps> = ({
  medicationData,
  onAddMedication,
}) => {
  const { medications, isLoading, onRemove } = medicationData;

  const methods = useForm<CurrentMedicationsProviderColumnForm>({
    defaultValues: {
      medication: null,
      dose: null,
      date: null,
      lastFilledDate: null,
      refills: null,
      type: 'scheduled',
      patientCouldNotConfirmDosage: false,
    },
  });
  const { isLoading: isChartDataLoading } = useChartData();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();

  const { control, reset, handleSubmit, setValue, watch } = methods;
  const isScheduled = watch('type') === 'scheduled';

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { isFetching: isSearching, data } = useGetMedicationsSearch(debouncedSearchTerm);
  const medSearchOptions = data || [];

  const medicationsMap: { scheduled: MedicationDTO[]; asNeeded: MedicationDTO[] } = useMemo(
    () => ({
      scheduled: medications.filter((med) => med.type === 'scheduled'),
      asNeeded: medications.filter((med) => med.type === 'as-needed'),
    }),
    [medications]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleInputChange = useCallback(
    debounce((data) => {
      console.log(data);
      if (data.length > 2) {
        setDebouncedSearchTerm(data);
      }
    }, 800),
    []
  );

  const handleFormSubmitted = async (data: CurrentMedicationsProviderColumnForm): Promise<void> => {
    if (data && data.medication) {
      const scheduled = data.type === 'scheduled';
      const refills = data.refills?.trim();
      const success = await onAddMedication({
        medication: data.medication,
        dose: data.dose,
        directions: null,
        type: data.type,
        date: data.date?.toUTC().toString(),
        patientCouldNotConfirmDosage: data.patientCouldNotConfirmDosage,
        lastFilledDate: scheduled ? data.lastFilledDate?.toUTC().toString() : undefined,
        refills: scheduled && refills ? Number(refills) : undefined,
      });
      if (success) {
        reset({
          medication: null,
          date: null,
          lastFilledDate: null,
          refills: null,
          dose: null,
          type: 'scheduled',
          patientCouldNotConfirmDosage: false,
        });
      }
    }
  };

  const { quickPicks: medicationHistoryQuickPicks, loading: medicationHistoryQuickPicksLoading } =
    useMergedMedicationHistoryQuickPicks();

  const handleQuickPickSelect = useCallback(
    (quickPick: { name: string; strength?: string; medicationId?: number }): void => {
      const quickPickAsMedication: ExtractObjectType<ErxSearchMedicationsResponse> = {
        name: quickPick.name,
        strength: quickPick.strength,
        id: quickPick.medicationId,
      } as ExtractObjectType<ErxSearchMedicationsResponse>;

      setValue('medication', quickPickAsMedication);
    },
    [setValue]
  );

  const commandPaletteItems = useMemo(
    () =>
      isReadOnly
        ? []
        : medicationHistoryQuickPicks.map((quickPick) => ({
            id: `medication-${quickPick.id ?? `${quickPick.name}-${quickPick.strength ?? ''}`}`,
            label: `${quickPick.name}${quickPick.strength ? ` (${quickPick.strength})` : ''}`,
            category: 'Add Medication',
            onSelect: () => handleQuickPickSelect(quickPick),
          })),
    [handleQuickPickSelect, isReadOnly, medicationHistoryQuickPicks]
  );
  useCommandPaletteSource('medication-quick-picks', commandPaletteItems);

  const handlePendingQuickPick = useCallback(
    (payload: MedicationHistoryQuickPickData) => {
      if (isReadOnly) {
        return;
      }

      handleQuickPickSelect(payload);
    },
    [handleQuickPickSelect, isReadOnly]
  );
  usePendingQuickPick('medications', handlePendingQuickPick);

  return (
    <Box>
      <Box
        data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsColumn}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: (medications.length || isChartDataLoading) && !isReadOnly ? 2 : 0,
        }}
      >
        {isChartDataLoading ? (
          <ProviderSideListSkeleton />
        ) : (
          <>
            {medicationsMap.scheduled.length > 0 ? (
              <Box data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsScheduledList}>
                <CurrentMedicationGroup
                  label="Scheduled medications"
                  medications={medicationsMap.scheduled}
                  onRemove={onRemove}
                  isLoading={isLoading}
                  dataTestId={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsList('scheduled')}
                ></CurrentMedicationGroup>
              </Box>
            ) : null}
            {medicationsMap.asNeeded.length > 0 ? (
              <Box data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsAsNeededList}>
                <CurrentMedicationGroup
                  label="As needed medications"
                  medications={medicationsMap.asNeeded}
                  onRemove={onRemove}
                  isLoading={isLoading}
                  dataTestId={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsList('as-needed')}
                ></CurrentMedicationGroup>
              </Box>
            ) : null}
          </>
        )}
      </Box>

      {medications.length === 0 && isReadOnly && !isChartDataLoading && (
        <Typography color="secondary.light">Missing. Patient input must be reconciled by provider</Typography>
      )}

      {!isReadOnly && (
        <form onSubmit={(event) => handleSubmit((data) => handleFormSubmitted(data))(event)}>
          <Card
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: otherColors.formCardBg,
              borderRadius: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(1, 1fr)',
              gap: 2,
            }}
          >
            <QuickPicksButton
              quickPicks={medicationHistoryQuickPicks}
              loading={medicationHistoryQuickPicksLoading}
              getLabel={(quickPick) => `${quickPick.name}${quickPick.strength ? ` (${quickPick.strength})` : ''}`}
              onSelect={handleQuickPickSelect}
              disabled={isLoading || isChartDataLoading}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <RadioGroup row value={value} onChange={onChange}>
                  <FormControlLabel
                    value="scheduled"
                    data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsScheduledRadioButton}
                    control={<Radio size="small" disabled={isLoading} />}
                    label={'Scheduled medication'}
                  />
                  <FormControlLabel
                    value="as-needed"
                    data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsAsNeededRadioButton}
                    control={<Radio size="small" disabled={isLoading} />}
                    label={'As needed medication'}
                  />
                </RadioGroup>
              )}
            ></Controller>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Controller
                name="medication"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Autocomplete
                    value={value}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? option
                        : `${option.name} ${option.strength ? `(${option.strength})` : ''}`
                    }
                    fullWidth
                    isOptionEqualToValue={(option, value) => value.id === option.id}
                    loading={isSearching}
                    size="small"
                    disablePortal
                    disabled={isLoading || isChartDataLoading}
                    noOptionsText={
                      debouncedSearchTerm && debouncedSearchTerm.length > 2 && medSearchOptions.length === 0
                        ? 'Nothing found for this search criteria'
                        : 'Start typing to load results'
                    }
                    options={medSearchOptions}
                    onChange={(_e, data) => {
                      onChange(data);
                    }}
                    sx={{ gridColumn: 'span 2' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onChange={(e) => debouncedHandleInputChange(e.target.value)}
                        data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsInput}
                        label="Medication"
                        placeholder="Search"
                        required={true}
                        error={!!error}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontWeight: 'bold',
                          },
                        }}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="dose"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsDoseInput}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    label="Recent dose amount and units"
                    placeholder="Provide amount and units"
                    error={!!error}
                  />
                )}
              ></Controller>
              <Controller
                name="patientCouldNotConfirmDosage"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        disabled={isLoading || isChartDataLoading}
                        size="small"
                      />
                    }
                    label="Patient could not confirm dosage"
                  />
                )}
              />
              <Controller
                name="date"
                control={control}
                rules={{
                  validate: (val) => {
                    if (val && !val.isValid) {
                      return val.invalidExplanation ?? 'Provide valid date time';
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Box
                    sx={{ gridColumn: 'span 2' }}
                    data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsDateTimeInput}
                  >
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <DateTimePicker
                        onChange={onChange}
                        value={value || null}
                        label="Last time medication was taken"
                        slotProps={{
                          textField: {
                            sx: { width: '100%' },
                            InputLabelProps: { shrink: true },
                            InputProps: { size: 'small', error: !!error },
                          },
                        }}
                      ></DateTimePicker>
                    </LocalizationProvider>
                  </Box>
                )}
              ></Controller>
              {isScheduled && (
                <>
                  <Controller
                    name="lastFilledDate"
                    control={control}
                    rules={{
                      validate: (val) => {
                        if (val && !val.isValid) {
                          return val.invalidExplanation ?? 'Provide valid date time';
                        }
                        return true;
                      },
                    }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Box
                        sx={{ gridColumn: 'span 2' }}
                        data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsLastFilledInput}
                      >
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                          <DateTimePicker
                            onChange={onChange}
                            value={value || null}
                            label="Prescription Last Filled"
                            slotProps={{
                              textField: {
                                sx: { width: '100%' },
                                InputLabelProps: { shrink: true },
                                InputProps: { size: 'small', error: !!error },
                              },
                            }}
                          ></DateTimePicker>
                        </LocalizationProvider>
                      </Box>
                    )}
                  ></Controller>
                  <Controller
                    name="refills"
                    control={control}
                    rules={{
                      validate: (val) => {
                        const trimmed = val?.trim();
                        if (trimmed && !REFILLS_PATTERN.test(trimmed)) {
                          return 'Enter a whole number from 0 to 99';
                        }
                        return true;
                      },
                    }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <TextField
                        value={value || ''}
                        onChange={onChange}
                        data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsRefillsInput}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        label="Number of Refills"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  ></Controller>
                </>
              )}
            </Box>
            <Button
              variant="outlined"
              type="submit"
              disabled={isLoading}
              data-testid={dataTestIds.telemedEhrFlow.hpiCurrentMedicationsAddButton}
              sx={{
                borderColor: otherColors.consentBorder,
                borderRadius: 100,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 14,
                maxWidth: '100px',
              }}
            >
              Add
            </Button>
          </Card>
        </form>
      )}
    </Box>
  );
};
